import React, { useState } from 'react';
import { 
  Users, 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  Edit, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock
} from 'lucide-react';

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phone: string;
  email: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
  };
  insurance: {
    provider: string;
    memberId: string;
    groupNumber: string;
    bin: string;
    pcn: string;
  };
  allergies: string[];
  medicalHistory: string[];
  lastVisit: string;
  totalPrescriptions: number;
  status: 'active' | 'inactive' | 'pending';
}

export function Patients() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedInsurance, setSelectedInsurance] = useState<string>('all');

  const mockPatients: Patient[] = [
    {
      id: '1',
      firstName: 'John',
      lastName: 'Smith',
      dateOfBirth: '1985-03-15',
      phone: '(555) 123-4567',
      email: 'john.smith@email.com',
      address: {
        street: '123 Main St',
        city: 'San Juan',
        state: 'PR',
        zipCode: '00901'
      },
      insurance: {
        provider: 'Triple S',
        memberId: 'TS123456789',
        groupNumber: 'GRP001',
        bin: '610014',
        pcn: 'PCN001'
      },
      allergies: ['Penicillin', 'Sulfa'],
      medicalHistory: ['Hypertension', 'Diabetes Type 2'],
      lastVisit: '2024-08-15',
      totalPrescriptions: 12,
      status: 'active'
    },
    {
      id: '2',
      firstName: 'Maria',
      lastName: 'Garcia',
      dateOfBirth: '1978-07-22',
      phone: '(555) 987-6543',
      email: 'maria.garcia@email.com',
      address: {
        street: '456 Oak Ave',
        city: 'Bayamon',
        state: 'PR',
        zipCode: '00961'
      },
      insurance: {
        provider: 'MCS',
        memberId: 'MCS987654321',
        groupNumber: 'GRP002',
        bin: '610015',
        pcn: 'PCN002'
      },
      allergies: ['Latex'],
      medicalHistory: ['Asthma', 'High Cholesterol'],
      lastVisit: '2024-08-10',
      totalPrescriptions: 8,
      status: 'active'
    },
    {
      id: '3',
      firstName: 'Carlos',
      lastName: 'Rodriguez',
      dateOfBirth: '1992-11-08',
      phone: '(555) 456-7890',
      email: 'carlos.rodriguez@email.com',
      address: {
        street: '789 Pine Rd',
        city: 'Caguas',
        state: 'PR',
        zipCode: '00725'
      },
      insurance: {
        provider: 'Humana',
        memberId: 'HUM456789123',
        groupNumber: 'GRP003',
        bin: '610016',
        pcn: 'PCN003'
      },
      allergies: [],
      medicalHistory: ['Depression'],
      lastVisit: '2024-08-05',
      totalPrescriptions: 5,
      status: 'active'
    }
  ];

  const filteredPatients = mockPatients.filter(patient => {
    const matchesSearch = 
      patient.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.phone.includes(searchTerm) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === 'all' || patient.status === selectedStatus;
    const matchesInsurance = selectedInsurance === 'all' || patient.insurance.provider === selectedInsurance;
    
    return matchesSearch && matchesStatus && matchesInsurance;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'inactive': return <Clock className="w-4 h-4" />;
      case 'pending': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-gray-900">Patient Management</h1>
          <p className="text-gray-600 mt-2">Manage patient profiles, insurance, and medical history</p>
        </div>
        <button className="bg-gradient-to-r from-[#4B0082] to-[#6B38FB] hover:from-[#6B38FB] hover:to-[#4B0082] text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
          <Plus className="w-5 h-5 inline mr-2" />
          New Patient
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Patients</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, phone, or email..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          {/* Insurance Filter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Insurance</label>
            <select
              value={selectedInsurance}
              onChange={(e) => setSelectedInsurance(e.target.value)}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#6B38FB] focus:border-transparent"
            >
              <option value="all">All Providers</option>
              <option value="Triple S">Triple S</option>
              <option value="MCS">MCS</option>
              <option value="Humana">Humana</option>
            </select>
          </div>
        </div>
      </div>

      {/* Patient Count */}
      <div className="bg-gradient-to-r from-[#4B0082]/10 to-[#6B38FB]/10 rounded-xl p-4 border border-[#6B38FB]/20">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[#6B38FB]">Total Patients</p>
            <p className="text-2xl font-bold text-[#4B0082]">{filteredPatients.length}</p>
          </div>
          <Users className="w-8 h-8 text-[#6B38FB]" />
        </div>
      </div>

      {/* Patients List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Patient</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Insurance</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Visit</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredPatients.map((patient) => (
                <tr key={patient.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div>
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-[#4B0082] to-[#6B38FB] rounded-full flex items-center justify-center text-white font-semibold">
                          {patient.firstName[0]}{patient.lastName[0]}
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-semibold text-gray-900">{patient.firstName} {patient.lastName}</p>
                          <p className="text-sm text-gray-500">DOB: {new Date(patient.dateOfBirth).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center text-sm text-gray-900">
                        <Phone className="w-4 h-4 mr-2 text-[#6B38FB]" />
                        {patient.phone}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Mail className="w-4 h-4 mr-2 text-[#6B38FB]" />
                        {patient.email}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-[#6B38FB]" />
                        {patient.address.city}, {patient.address.state}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{patient.insurance.provider}</p>
                      <p className="text-sm text-gray-500">ID: {patient.insurance.memberId}</p>
                      <p className="text-sm text-gray-500">Group: {patient.insurance.groupNumber}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(patient.status)}`}>
                      {getStatusIcon(patient.status)}
                      <span className="ml-1 capitalize">{patient.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-900">
                      <Calendar className="w-4 h-4 mr-2 text-[#6B38FB]" />
                      {new Date(patient.lastVisit).toLocaleDateString()}
                    </div>
                    <p className="text-sm text-gray-500">{patient.totalPrescriptions} prescriptions</p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="p-2 text-[#6B38FB] hover:bg-[#6B38FB]/10 rounded-lg transition-colors">
                        <Shield className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
